export interface GestaoConcursoFeature {
  title: string;
  description: string;
  icon: string;
  integration: string;
  actions: string[];
  status?: 'ready' | 'pending';
}

export interface GestaoConcursoOverview {
  title: string;
  subtitle: string;
  icon: string;
  features: GestaoConcursoFeature[];
}

export const GESTAO_CONCURSO_OVERVIEWS: Record<
  string,
  GestaoConcursoOverview
> = {
  candidatos: {
    title: 'Candidatos',
    subtitle:
      'Gerir o perfil, a formação e toda a informação profissional dos candidatos.',
    icon: 'pi pi-users',
    features: [
      {
        title: 'Perfil e dados pessoais',
        description:
          'Registo e actualização da identificação, filiação, contactos, morada e fotografia.',
        icon: 'pi pi-user-edit',
        integration: 'PerfilCandidato',
        actions: ['Pesquisar por NIF', 'Criar perfil', 'Actualizar dados'],
      },
      {
        title: 'Habilitações académicas',
        description:
          'Formação académica, instituição, curso, nível e respectivos comprovativos.',
        icon: 'pi pi-graduation-cap',
        integration: 'HabLiteraria',
        actions: ['Listar habilitações', 'Adicionar formação', 'Editar e remover'],
      },
      {
        title: 'Experiência profissional',
        description:
          'Histórico profissional, funções, entidades empregadoras e períodos de actividade.',
        icon: 'pi pi-briefcase',
        integration: 'ExpProfissional',
        actions: ['Listar experiências', 'Adicionar experiência', 'Editar e remover'],
      },
      {
        title: 'Competências',
        description:
          'Competências técnicas e comportamentais associadas ao perfil do candidato.',
        icon: 'pi pi-star',
        integration: 'Competencia',
        actions: ['Pesquisar', 'Associar ao perfil', 'Actualizar domínio'],
      },
      {
        title: 'Idiomas',
        description:
          'Idiomas dominados e respectivos níveis de leitura, escrita e conversação.',
        icon: 'pi pi-language',
        integration: 'Idioma / IdiomaNivel',
        actions: ['Listar idiomas', 'Definir nível', 'Editar e remover'],
      },
      {
        title: 'Documentos do candidato',
        description:
          'Documentos de identificação e anexos utilizados durante as candidaturas.',
        icon: 'pi pi-folder-open',
        integration: 'AnexoCandidatura',
        actions: ['Anexar documento', 'Consultar', 'Descarregar'],
      },
    ],
  },
  comissoes: {
    title: 'Comissões e júris',
    subtitle:
      'Configurar as comissões de trabalho, os seus membros e as responsabilidades de avaliação.',
    icon: 'pi pi-sitemap',
    features: [
      {
        title: 'Comissões de trabalho',
        description:
          'Criação e organização das comissões responsáveis por cada concurso.',
        icon: 'pi pi-users',
        integration: 'ComissaoTrabalho',
        actions: ['Listar comissões', 'Criar comissão', 'Editar e eliminar'],
      },
      {
        title: 'Integrantes',
        description:
          'Associação dos membros às comissões e gestão da sua participação.',
        icon: 'pi pi-user-plus',
        integration: 'ComissaoIntegrante',
        actions: ['Adicionar membro', 'Definir participação', 'Remover membro'],
      },
      {
        title: 'Funções dos membros',
        description:
          'Definição dos papéis, responsabilidades e funções exercidas na comissão.',
        icon: 'pi pi-id-card',
        integration: 'FuncaoMembroComissao',
        actions: ['Listar funções', 'Criar função', 'Editar e eliminar'],
      },
      {
        title: 'Avaliações e pareceres',
        description:
          'Análise das candidaturas, registo das avaliações e emissão de pareceres.',
        icon: 'pi pi-file-edit',
        integration: 'ComissaoTrabalho / Candidatura',
        actions: ['Avaliar candidatura', 'Emitir parecer', 'Consultar histórico'],
      },
    ],
  },
  provas: {
    title: 'Gestão de provas',
    subtitle:
      'Preparar perguntas, testes, salas e equipas responsáveis pela realização das provas.',
    icon: 'pi pi-file-edit',
    features: [
      {
        title: 'Banco de questões',
        description:
          'Perguntas, respostas possíveis, soluções e pontuações utilizadas nos testes.',
        icon: 'pi pi-question-circle',
        integration: 'BasePerguntaResposta / OpcoesBasePergunta',
        actions: ['Pesquisar perguntas', 'Criar pergunta', 'Gerir opções'],
      },
      {
        title: 'Salas',
        description:
          'Configuração das salas, capacidade, localização e condições de realização.',
        icon: 'pi pi-building',
        integration: 'Sala',
        actions: ['Listar salas', 'Criar sala', 'Editar e eliminar'],
      },
      {
        title: 'Avaliadores',
        description:
          'Associação dos avaliadores responsáveis por cada sala e sessão de prova.',
        icon: 'pi pi-user',
        integration: 'SalaAvaliador',
        actions: ['Associar avaliador', 'Consultar equipa', 'Remover associação'],
      },
      {
        title: 'Testes dos candidatos',
        description:
          'Distribuição, realização, controlo e acompanhamento dos testes submetidos.',
        icon: 'pi pi-verified',
        integration: 'CandidatoTeste',
        actions: ['Distribuir testes', 'Acompanhar realização', 'Consultar resultado'],
      },
      {
        title: 'Controlo da prova',
        description:
          'Abertura e fecho das salas, ocorrências, presença e auditoria do exame.',
        icon: 'pi pi-shield',
        integration: 'Sala / ExamAudit',
        actions: ['Controlar sessão', 'Registar ocorrência', 'Consultar auditoria'],
      },
    ],
  },
  apuramento: {
    title: 'Apuramento',
    subtitle:
      'Registar classificações, avaliar por fase e consolidar os resultados dos concursos.',
    icon: 'pi pi-chart-bar',
    features: [
      {
        title: 'Notas e médias',
        description:
          'Registo das notas e cálculo das médias obtidas por candidato e por prova.',
        icon: 'pi pi-calculator',
        integration: 'Apuramento',
        actions: ['Registar nota', 'Calcular média', 'Rever classificação'],
      },
      {
        title: 'Avaliação por fase',
        description:
          'Apuramento progressivo dos candidatos em cada fase configurada no concurso.',
        icon: 'pi pi-sort-amount-up',
        integration: 'ConcursoFase / Apuramento',
        actions: ['Seleccionar fase', 'Avaliar candidatos', 'Fechar fase'],
      },
      {
        title: 'Aprovação e reprovação',
        description:
          'Classificação final dos candidatos de acordo com os critérios estabelecidos.',
        icon: 'pi pi-check-square',
        integration: 'Apuramento / CandidatoTeste',
        actions: ['Apurar aprovados', 'Apurar reprovados', 'Validar decisão'],
      },
      {
        title: 'Resultados finais',
        description:
          'Consolidação, consulta e publicação dos resultados finais do concurso.',
        icon: 'pi pi-megaphone',
        integration: 'Apuramento',
        actions: ['Consolidar resultado', 'Publicar', 'Exportar'],
      },
    ],
  },
  reclamacoes: {
    title: 'Reclamações e recursos',
    subtitle:
      'Receber, analisar e decidir as reclamações apresentadas pelos candidatos.',
    icon: 'pi pi-comments',
    features: [
      {
        title: 'Reclamações submetidas',
        description:
          'Caixa de entrada com pesquisa, estado e detalhe das reclamações recebidas.',
        icon: 'pi pi-inbox',
        integration: 'Reclamacao',
        actions: ['Pesquisar', 'Consultar detalhe', 'Alterar estado'],
      },
      {
        title: 'Documentos anexos',
        description:
          'Comprovativos e documentos que suportam a reclamação ou a respectiva análise.',
        icon: 'pi pi-paperclip',
        integration: 'AnexoReclamacao',
        actions: ['Consultar anexos', 'Adicionar documento', 'Descarregar'],
      },
      {
        title: 'Comunicação e parecer',
        description:
          'Troca de mensagens entre o candidato e a comissão, com emissão de parecer.',
        icon: 'pi pi-send',
        integration: 'Reclamacao / Mensagem',
        actions: ['Responder', 'Solicitar informação', 'Emitir parecer'],
      },
      {
        title: 'Decisão final',
        description:
          'Registo da decisão, fundamentação e comunicação do resultado ao candidato.',
        icon: 'pi pi-file-check',
        integration: 'Reclamacao / Notification',
        actions: ['Registar decisão', 'Notificar candidato', 'Encerrar processo'],
      },
    ],
  },
  auditoria: {
    title: 'Auditoria',
    subtitle:
      'Acompanhar as operações realizadas, os responsáveis e as alterações efectuadas.',
    icon: 'pi pi-history',
    features: [
      {
        title: 'Auditoria das provas',
        description:
          'Consulta dos eventos ocorridos durante a abertura, realização e fecho das provas.',
        icon: 'pi pi-shield',
        integration: 'Sala / ExamAudit',
        actions: ['Pesquisar eventos', 'Consultar utilizador', 'Ver data e hora'],
      },
      {
        title: 'Auditoria geral',
        description:
          'Histórico transversal das operações, tipo de acção e dados alterados no sistema.',
        icon: 'pi pi-list-check',
        integration: 'Endpoint não publicado no Swagger',
        actions: ['Filtrar operações', 'Comparar alterações', 'Exportar registo'],
        status: 'pending',
      },
    ],
  },
  catalogos: {
    title: 'Catálogos e configuração',
    subtitle:
      'Administrar os dados de suporte utilizados na publicação e execução dos concursos.',
    icon: 'pi pi-cog',
    features: [
      {
        title: 'Tipos de concurso',
        description: 'Modalidades e classificações disponíveis para novos concursos.',
        icon: 'pi pi-tags',
        integration: 'TipoConcurso',
        actions: ['Listar tipos', 'Criar tipo', 'Editar e eliminar'],
      },
      {
        title: 'Categorias e especialidades',
        description:
          'Estrutura de categorias, especialidades e associação das vagas do concurso.',
        icon: 'pi pi-th-large',
        integration: 'CategoriaEspecialidade / ConcursoCategoriaEspecialidade',
        actions: ['Gerir categorias', 'Associar especialidades', 'Configurar vagas'],
      },
      {
        title: 'Fases',
        description:
          'Etapas do concurso, ordem, datas, duração e regras de progressão.',
        icon: 'pi pi-sort-alt',
        integration: 'Fase / ConcursoFase / AddDaysToFases',
        actions: ['Criar fase', 'Associar ao concurso', 'Configurar datas'],
      },
      {
        title: 'Critérios de selecção',
        description:
          'Critérios gerais, critérios do concurso e respectivas regras de validação.',
        icon: 'pi pi-check-circle',
        integration: 'CriterioGeral / CriterioConcurso / CriterioValidacao',
        actions: ['Gerir critérios', 'Configurar validação', 'Associar ao concurso'],
      },
      {
        title: 'Locais e horários',
        description:
          'Locais de distribuição, horários e associação às fases e especialidades.',
        icon: 'pi pi-map-marker',
        integration: 'LocalDistribuicao / Horario',
        actions: ['Gerir locais', 'Definir horários', 'Associar à fase'],
      },
      {
        title: 'Editais e documentos',
        description:
          'Publicação, consulta e descarregamento dos documentos oficiais do concurso.',
        icon: 'pi pi-file-pdf',
        integration: 'Concurso',
        actions: ['Anexar edital', 'Consultar documentos', 'Descarregar'],
      },
      {
        title: 'Idiomas e países',
        description:
          'Dados de referência utilizados no perfil e nas candidaturas.',
        icon: 'pi pi-globe',
        integration: 'Idioma / IdiomaNivel / Pais',
        actions: ['Gerir idiomas', 'Gerir níveis', 'Consultar países'],
      },
    ],
  },
};
